import { useNavigate } from "react-router-dom";
import { useLessons } from "@/hooks/useLessons";
import LessonArtwork from "@/components/LessonArtwork";
import { resolveLessonPrice, useLessonsPromoUntil } from "@/lib/lessonsPromo";

const lessonBenefit = (description: string | null) => {
  if (!description) return "Praktinė audio pamoka aiškesniam santykiui su savimi.";
  return description.match(/^.*?[.!?](?:\s|$)/)?.[0]?.trim() ?? description.trim();
};

const LectureGrid = () => {
  const { data: lessons = [] } = useLessons();
  const navigate = useNavigate();
  const promoUntil = useLessonsPromoUntil();

  // sort by available_until so currently active ones first
  const sorted = [...lessons].sort((a, b) => {
    const aActive = a.available_until && new Date(a.available_until) > new Date();
    const bActive = b.available_until && new Date(b.available_until) > new Date();
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    return 0;
  });

  return (
    <section className="bg-cream py-16 md:py-24">
      <div className="container mx-auto px-6">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-marquee text-4xl md:text-5xl font-bold text-ink">Visos pamokos</h2>
          <span className="text-sm text-ink/55">{sorted.length} pamokos</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {sorted.map((lesson, i) => {
            const pricing = resolveLessonPrice(lesson, promoUntil);
            const regular = pricing.regularPrice;
            const current = pricing.amount;
            const priceStr = current.toFixed(2).replace(".", ",");
            const regularStr = regular.toFixed(2).replace(".", ",");
            const onSale = pricing.onSale;
            const percentOff = pricing.discountPercent;
            return (
              <button
                key={lesson.id}
                onClick={() => navigate(`/pamokos?lesson=${lesson.id}`)}
                className="relative group text-left rounded-3xl overflow-hidden min-h-[430px] flex flex-col bg-cream-soft transition-all duration-200 hover:-translate-y-1 hover:shadow-xl"
              >
                {/* Full-bleed editorial artwork */}
                <div className="relative flex-1 min-h-0">
                  <LessonArtwork seed={lesson.id} title={lesson.title} className="absolute inset-0" />
                  {onSale && (
                    <span className="absolute top-3 right-3 inline-flex items-center px-2.5 py-1 rounded-full bg-ink text-cream text-[10px] tracking-[0.18em] uppercase font-bold">
                      −{percentOff}%
                    </span>
                  )}
                </div>

                {/* Caption block — always cream, ink type */}
                <div className="bg-cream-soft text-ink p-5 min-h-[190px] flex flex-col">
                  <div className="text-[11px] tracking-[0.22em] uppercase text-ink/55 mb-1">
                    Pamoka {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-semibold text-base leading-snug uppercase tracking-wide mb-2 line-clamp-2">
                    {lesson.title}
                  </div>
                  <p className="text-sm text-ink/65 leading-relaxed line-clamp-2 mb-4">
                    {lessonBenefit(lesson.description)}
                  </p>
                  <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    <span className="text-ink/60">{lesson.duration ?? "Audio pamoka"}</span>
                    {onSale ? (
                      <>
                        <span className="text-red font-bold text-sm">€{priceStr}</span>
                        <span className="text-ink/40 line-through">€{regularStr}</span>
                      </>
                    ) : (
                      <span className="text-ink/65">€{priceStr}</span>
                    )}
                    <span className="text-ink/60">Neribota prieiga</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default LectureGrid;
